import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import {
  DiagonalStripes,
  CrosshairMark,
  TriangleAccent,
  HandDrawnUnderline,
  GridDots,
} from './GeometricPatterns';

const VEHICLE_CONFIG = {
  ev: {
    label: 'Elektrisk',
    icon: 'Zap',
    color: '#FF5722',
    bg: 'rgba(255,87,34,0.12)',
    border: 'rgba(255,87,34,0.35)',
    discountLabel: 'EV-rabatt 50%',
  },
  hybrid: {
    label: 'Hybrid',
    icon: 'Leaf',
    color: '#FF5722',
    bg: 'rgba(255,87,34,0.08)',
    border: 'rgba(255,87,34,0.25)',
    discountLabel: 'Hybrid-rabatt 25%',
  },
  standard: {
    label: 'Standard',
    icon: 'Car',
    color: '#004F98',
    bg: 'rgba(0,79,152,0.1)',
    border: 'rgba(0,79,152,0.3)',
    discountLabel: null,
  },
};

const formatNOK = (amount) =>
  new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', minimumFractionDigits: 2 })?.format(amount);

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d?.toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

const TripCard = ({ trip, index = 0, onDelete }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const vehicle = VEHICLE_CONFIG?.[trip?.vehicleType] || VEHICLE_CONFIG?.standard;
  const isOffset = index % 2 === 1;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 80);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (cardRef?.current) observer?.observe(cardRef?.current);
    return () => observer?.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      style={{
        transform: isVisible
          ? 'translateY(0) translateX(0)'
          : `translateY(40px) translateX(${isOffset ? '12px' : '-12px'})`,
        opacity: isVisible ? 1 : 0,
        transition: `transform 600ms cubic-bezier(0.34, 1.2, 0.64, 1), opacity 500ms ease-out`,
        transitionDelay: `${index * 60}ms`,
        marginLeft: isOffset ? '24px' : '0px',
        marginRight: isOffset ? '0px' : '24px',
      }}
    >
      <div
        className="relative overflow-hidden"
        style={{
          background: 'var(--color-card)',
          border: '2px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          borderLeft: `3px solid ${vehicle?.color}`,
          transition: 'border-color 200ms ease-out, box-shadow 200ms ease-out, transform 200ms cubic-bezier(0.22,1,0.36,1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = vehicle?.color;
          e.currentTarget.style.boxShadow = `0 12px 36px rgba(0,0,0,0.3), -3px 0 0 ${vehicle?.color}`;
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0px)';
          e.currentTarget.style.borderLeft = `3px solid ${vehicle?.color}`;
        }}
      >
        {/* Background geometric decorations */}
        <DiagonalStripes
          className="top-0 right-0 opacity-40"
          color={vehicle?.color}
          opacity={0.06}
        />
        <GridDots
          className="bottom-0 left-0"
          rows={3}
          cols={5}
          color="#004F98"
          opacity={0.1}
        />
        <TriangleAccent
          className="top-2 right-2"
          size={28}
          color={vehicle?.color}
        />

        <div className="p-5 md:p-6">
          {/* Header row */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              {/* Date */}
              <div
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: '18px',
                  letterSpacing: '0.02em',
                  color: 'var(--color-foreground)',
                  marginBottom: '2px',
                }}
              >
                {formatDate(trip?.date)}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-caption)',
                  fontSize: '12px',
                  color: 'var(--color-muted-foreground)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Icon name="Clock" size={11} />
                {formatTime(trip?.date)}
                {trip?.duration && (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <Icon name="Route" size={11} />
                    {trip?.duration}
                  </>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Vehicle badge */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 10px',
                  background: vehicle?.bg,
                  border: `1.5px solid ${vehicle?.border}`,
                  borderRadius: 'var(--radius-sm)',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                <CrosshairMark
                  className="-top-1 -left-1"
                  size={10}
                  color={vehicle?.color}
                />
                <Icon name={vehicle?.icon} size={13} color={vehicle?.color} />
                <span
                  style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: vehicle?.color,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {vehicle?.label}
                </span>
              </div>

              {/* Delete button */}
              {onDelete && (
                <button
                  onClick={(e) => { e?.stopPropagation(); onDelete(trip); }}
                  aria-label="Slett reise"
                  style={{
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,87,34,0.08)',
                    border: '1.5px solid rgba(255,87,34,0.25)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 180ms ease-out',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,87,34,0.18)'; e.currentTarget.style.borderColor = 'rgba(255,87,34,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,87,34,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,87,34,0.25)'; }}
                >
                  <Icon name="Trash2" size={14} color="#FF5722" />
                </button>
              )}
            </div>
          </div>

          {/* Route info */}
          <div
            className="relative mb-4"
            style={{
              background: 'rgba(0,79,152,0.05)',
              border: '1px solid rgba(0,79,152,0.15)',
              borderRadius: 'var(--radius-sm)',
              padding: '12px 14px',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-1" style={{ flexShrink: 0 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#10B981',
                    border: '2px solid rgba(16,185,129,0.3)',
                  }}
                />
                <div
                  style={{
                    width: 1,
                    height: 20,
                    background: 'linear-gradient(to bottom, #10B981, #004F98)',
                    opacity: 0.5,
                  }}
                />
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '2px',
                    background: '#004F98',
                    border: '2px solid rgba(0,79,152,0.3)',
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: 'var(--color-foreground)',
                    marginBottom: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontStyle: 'italic',
                  }}
                >
                  {trip?.departure || 'Ukjent avgang'}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: 'var(--color-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontStyle: 'italic',
                  }}
                >
                  {trip?.destination || 'Ukjent destinasjon'}
                </div>
              </div>
              {trip?.distance && (
                <div
                  style={{
                    flexShrink: 0,
                    fontFamily: 'var(--font-data)',
                    fontSize: '12px',
                    color: 'var(--color-muted-foreground)',
                    textAlign: 'right',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--color-foreground)' }}>
                    {trip?.distance} km
                  </div>
                  <div>distanse</div>
                </div>
              )}
            </div>
          </div>

          {/* Cost row */}
          <div className="flex items-end justify-between gap-4">
            <div>
              {trip?.discount > 0 && (
                <div className="flex items-center gap-2 mb-1">
                  <span
                    style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '12px',
                      color: 'var(--color-muted-foreground)',
                      textDecoration: 'line-through',
                    }}
                  >
                    {formatNOK(trip?.originalCost)}
                  </span>
                  {vehicle?.discountLabel && (
                    <span
                      style={{
                        fontFamily: 'var(--font-caption)',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#FF5722',
                        background: 'rgba(255,87,34,0.1)',
                        border: '1px solid rgba(255,87,34,0.25)',
                        borderRadius: '2px',
                        padding: '1px 6px',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {vehicle?.discountLabel}
                    </span>
                  )}
                </div>
              )}
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <div
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontWeight: 500,
                    fontSize: '22px',
                    color: 'var(--color-foreground)',
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {formatNOK(trip?.totalCost)}
                </div>
                <HandDrawnUnderline width={100} className="mt-1" />
              </div>
              {trip?.discount > 0 && (
                <div
                  style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#10B981',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icon name="TrendingDown" size={12} />
                  Spart {formatNOK(trip?.discount)}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {trip?.tollPoints?.length > 0 && (
                <span
                  style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '12px',
                    color: 'var(--color-muted-foreground)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icon name="MapPin" size={12} />
                  {trip?.tollPoints?.length} bomstasjoner
                </span>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '6px 12px',
                  background: 'rgba(0,79,152,0.08)',
                  border: '1.5px solid rgba(0,79,152,0.2)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-caption)',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--color-primary)',
                  cursor: 'pointer',
                  transition: 'all 150ms ease-out',
                }}
              >
                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={13} />
                {isExpanded ? 'Skjul' : 'Detaljer'}
              </button>
            </div>
          </div>

          {/* Expanded toll points */}
          {isExpanded && trip?.tollPoints?.length > 0 && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--color-border)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-caption)',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-muted-foreground)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: '10px',
                }}
              >
                Bomstasjoner
              </div>
              <div className="space-y-2">
                {trip?.tollPoints?.map((point, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: 'rgba(156,163,175,0.04)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '13px',
                          fontWeight: 500,
                          color: 'var(--color-foreground)',
                        }}
                      >
                        {point?.name}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-caption)',
                          fontSize: '11px',
                          color: 'var(--color-muted-foreground)',
                        }}
                      >
                        {point?.location}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div
                        style={{
                          fontFamily: 'var(--font-data)',
                          fontWeight: 500,
                          fontSize: '14px',
                          color: 'var(--color-foreground)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatNOK(point?.cost)}
                      </div>
                      {point?.originalCost && point?.originalCost !== point?.cost && (
                        <div
                          style={{
                            fontFamily: 'var(--font-caption)',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#FF5722',
                          }}
                        >
                          -{formatNOK(point?.originalCost - point?.cost)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripCard;
